import { useState, useEffect, useCallback, useRef } from "react";
import { compileFormSpec, type CompileResult, type DiagnosticMessage } from "../lib/compiler";
import type { FormSpec, FormElement } from "@formspec/core";
import type { FormIR } from "@formspec/core/internals";
import type { JsonSchema2020, UISchema, ValidationDiagnostic } from "@formspec/build/browser";
import type { ConstraintsConfig } from "../components/Constraints";
import { toConstraintConfig } from "../lib/constraintAdapter";

type PlaygroundLinterModule = typeof import("../lib/linter");

let linterModulePromise: Promise<PlaygroundLinterModule | null> | null = null;
let hasLoggedLinterFailure = false;

async function loadPlaygroundLinter(): Promise<PlaygroundLinterModule | null> {
  linterModulePromise ??= import("../lib/linter").catch((error: unknown) => {
    if (!hasLoggedLinterFailure) {
      console.warn("Playground linting is unavailable in this browser environment.", error);
      hasLoggedLinterFailure = true;
    }

    return null;
  });

  return linterModulePromise;
}

async function collectLintErrors(
  code: string,
  constraints: ConstraintsConfig
): Promise<DiagnosticMessage[]> {
  const linterModule = await loadPlaygroundLinter();
  if (!linterModule) {
    return [];
  }

  try {
    const lintMessages = linterModule.lintFormSpec(code, constraints);
    return lintMessages.map(
      (msg): DiagnosticMessage => ({
        message: msg.message,
        line: msg.line,
        column: msg.column,
        severity: msg.severity,
      })
    );
  } catch (error) {
    if (!hasLoggedLinterFailure) {
      console.warn("Playground linting failed at runtime and has been disabled.", error);
      hasLoggedLinterFailure = true;
    }

    return [];
  }
}

export interface UseFormspecCompilationOptions {
  /** Debounce delay in milliseconds (default: 500) */
  debounceMs?: number;
  /** Constraint configuration for validation (UI format) */
  constraints?: ConstraintsConfig;
}

export interface UseFormspecCompilationResult {
  /** Whether compilation is in progress */
  isCompiling: boolean;
  /** The compiled FormSpec object, if successful */
  formSpec: FormSpec<readonly FormElement[]> | null;
  /** The generated JSON Schema, if successful */
  jsonSchema: JsonSchema2020 | null;
  /** The generated UI Schema, if successful */
  uiSchema: UISchema | null;
  /** The canonical FormIR, if compilation succeeded */
  ir: FormIR | null;
  /** IR-level constraint validation diagnostics */
  irDiagnostics: readonly ValidationDiagnostic[];
  /** Compilation errors, if any */
  errors: DiagnosticMessage[];
  /** Manually trigger recompilation */
  recompile: () => void;
}

/**
 * Hook that handles FormSpec compilation with debouncing.
 *
 * @param code - The TypeScript source code to compile
 * @param options - Compilation options
 */
export function useFormspecCompilation(
  code: string,
  options: UseFormspecCompilationOptions = {}
): UseFormspecCompilationResult {
  const { debounceMs = 500, constraints } = options;
  const [isCompiling, setIsCompiling] = useState(false);
  const [formSpec, setFormSpec] = useState<FormSpec<readonly FormElement[]> | null>(null);
  const [jsonSchema, setJsonSchema] = useState<JsonSchema2020 | null>(null);
  const [uiSchema, setUiSchema] = useState<UISchema | null>(null);
  const [ir, setIr] = useState<FormIR | null>(null);
  const [irDiagnostics, setIrDiagnostics] = useState<readonly ValidationDiagnostic[]>([]);
  const [errors, setErrors] = useState<DiagnosticMessage[]>([]);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleCallbackRef = useRef<number | null>(null);
  const compileVersionRef = useRef(0);
  const codeRef = useRef(code);
  const constraintsRef = useRef(constraints);
  codeRef.current = code;
  constraintsRef.current = constraints;

  const compile = useCallback(() => {
    // Cancel any pending requestIdleCallback to prevent race conditions
    if (idleCallbackRef.current !== null && "cancelIdleCallback" in window) {
      window.cancelIdleCallback(idleCallbackRef.current);
      idleCallbackRef.current = null;
    }

    setIsCompiling(true);
    const compileVersion = ++compileVersionRef.current;

    // Use requestIdleCallback or setTimeout for non-blocking compilation
    const doCompile = async () => {
      idleCallbackRef.current = null;
      const currentConstraints = constraintsRef.current;

      // First, run ESLint linting if constraints are configured
      let lintErrors: DiagnosticMessage[] = [];
      if (currentConstraints) {
        lintErrors = await collectLintErrors(codeRef.current, currentConstraints);
      }

      // Then run compilation (TypeScript transpile + execute + schema generation)
      const constraintConfig = currentConstraints
        ? toConstraintConfig(currentConstraints)
        : undefined;
      const result: CompileResult = compileFormSpec(codeRef.current, {
        constraints: constraintConfig,
      });

      if (compileVersion !== compileVersionRef.current) {
        return;
      }

      if (result.success) {
        setFormSpec(result.formSpec);
        setJsonSchema(result.jsonSchema);
        setUiSchema(result.uiSchema);
        setIr(result.ir);
        setIrDiagnostics(result.irDiagnostics);
        // Show lint errors even if compilation succeeded
        setErrors(lintErrors);
      } else {
        // Combine lint errors with compilation errors
        setErrors([...lintErrors, ...result.errors]);
        // Keep previous successful results visible
      }

      setIsCompiling(false);
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ("requestIdleCallback" in window) {
      idleCallbackRef.current = window.requestIdleCallback(
        () => {
          void doCompile();
        },
        { timeout: 1000 }
      );
    } else {
      setTimeout(() => {
        void doCompile();
      }, 0);
    }
  }, []);

  // Debounced compilation effect
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      compile();
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (idleCallbackRef.current !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleCallbackRef.current);
      }
    };
  }, [code, constraints, debounceMs, compile]);

  // Initial compilation - we deliberately only run once on mount
  useEffect(() => {
    compile();
  }, [compile]);

  return {
    isCompiling,
    formSpec,
    jsonSchema,
    uiSchema,
    ir,
    irDiagnostics,
    errors,
    recompile: compile,
  };
}
